import type { MouseEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { Breakpoint, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import { toSentenceCase } from '@noths/polaris-client-utils';

import arrowBack from 'src/assets/svg/arrowBack.svg';
import type { TreeProps } from 'src/components/organisms/Filter/containers/TreeContainer.types';
import * as styles from 'src/components/organisms/Filter/styles/Tree.styles';
import type { ChangedFilterDetails } from 'src/components/organisms/Filter/types';
import type {
  BrowseDataAPIFilter,
  FilterOptionValue,
} from 'src/services/browse-data-api/types/BrowseDataAPIFilter';
import { FilterOptionsExpandableContent } from '../FilterOptionsExpandableContent';
import { splitTreeFilterOptionsByDepth } from './splitTreeFilterOptionsByDepth';

export const Tree = ({
  filter,
  pathWithoutCurrentFilter,
  onExpandableContentToggle,
  onFilterOptionChange,
}: TreeProps) => {
  const { parentNodes, childNodes, depth: initialDepth } = splitTreeFilterOptionsByDepth(filter);
  const [activeOptionDepth, setActiveOptionDepth] = useState(initialDepth);
  const prevDepthRef = useRef(initialDepth);
  const totalBranches = parentNodes.length + childNodes.length;
  const isOnSmallScreen = useBreakpoint() === Breakpoint.S;
  const isExpandable = !isOnSmallScreen && totalBranches > 6;
  const isTreeRoot = parentNodes.every((x) => x.depth === 1);

  useEffect(() => {
    prevDepthRef.current = activeOptionDepth;
  }, [activeOptionDepth]);

  const onClick = (
    event: MouseEvent<HTMLAnchorElement>,
    value: FilterOptionValue,
    depth: number,
    filter: BrowseDataAPIFilter,
    onFilterOptionChange: (filterOptionDetails: ChangedFilterDetails) => void,
  ) => {
    event.preventDefault();

    setActiveOptionDepth(depth);

    const activatedOption = filter.options.find((option) => option.value === value);

    onFilterOptionChange({
      title: filter.title,
      uid: filter.uid,
      options: [
        {
          active: true,
          depth: { current: depth, previous: prevDepthRef.current },
          title: activatedOption?.title,
          value: value,
        },
      ],
    });
  };

  return (
    <FilterOptionsExpandableContent
      filter={filter}
      onExpandableContentToggle={onExpandableContentToggle}
      isExpandable={isExpandable}
    >
      <ul css={styles.list}>
        {parentNodes.map(({ active, value, title, depth }) => (
          <li key={value}>
            <a
              href={`${pathWithoutCurrentFilter}&${filter.uid}=${value}`}
              css={styles.parentNodeLink}
              onClick={(e) => onClick(e, value, depth, filter, onFilterOptionChange)}
              data-is-active={active}
              aria-label={`${title}${active ? ' (currently selected)' : ''}`}
            >
              {!active && !isOnSmallScreen && !isTreeRoot && (
                <img src={arrowBack} alt="" width={16} height={16} />
              )}
              {toSentenceCase(title)}
            </a>
            {active && childNodes.length > 0 && (
              <ul aria-label={`${toSentenceCase(title)} sub-categories`} css={styles.list}>
                {childNodes.map(({ value, depth, title }) => (
                  <li key={value}>
                    <a
                      href={`${pathWithoutCurrentFilter}&${filter.uid}=${value}`}
                      css={styles.childNodeLink}
                      onClick={(e) => onClick(e, value, depth, filter, onFilterOptionChange)}
                    >
                      {toSentenceCase(title)}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </FilterOptionsExpandableContent>
  );
};
